import React, { useEffect } from 'react';
import { Card, Space, Typography } from 'antd';
import { useParams } from 'react-router-dom';
import { motion, Variants } from 'framer-motion'; // Import framer-motion

import useIsMobile from '@/hooks/useIsMobile';
import HotelRoomOverview from '@/modules/booking/components/HotelRoomOverview';
import HotelHeroCarousel from '@/modules/booking/components/HotelHeroCarousel';
import HotelIntro from '@/modules/booking/components/HotelIntro';
import { bookingClear } from '@/AppRedux/actions/booking'
import { paymentReset } from '@/AppRedux/actions';
import HotelProfileStateHook from '@/hooks/useHotelProfileState';
import BookingSearchCard from '@/modules/booking/components/BookingSearchCard';
import { useIntl } from 'react-intl';
import { FacilitiesList } from '@/modules/booking/components/FacilityList';
import GoogleMapMockup from '@/modules/booking/components/GoogleMap';
import HotelContactInfo from '@/modules/booking/components/HotelContact';
import { isEmpty } from 'lodash';
import { useAppDispatch } from '@/AppRedux/store/hooks';
import HotelPhotoGrid from '@/modules/booking/components/HotelPhotoGrid';

const { Title } = Typography;
// Animation Variants
const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2, // ให้ลูกๆ โผล่มาทีละ 0.2 วินาที
      delayChildren: 0.3,
    },
  },
};

const itemVariants: Variants = {
  hidden: { y: 30, opacity: 0 }, // เริ่มต้นต่ำกว่าปกติ 30px และจาง
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: 'spring',
      stiffness: 50,
      damping: 20, // ให้ความรู้สึกนุ่ม ไม่เด้งดึ๋ง
      duration: 0.8
    },
  },
};

const HotelLanding:React.FC = () => {
  const isMobile = useIsMobile(768);
  const dispatch = useAppDispatch();
  const { propcode } = useParams<{ propcode: string }>();

  const { dataSource } = HotelProfileStateHook()
  const { hotelBG, hotelRooms, hotelIntro, mapUrlEmbed } = dataSource || {}

  const intl = useIntl();
  document.title = propcode ? `${hotelIntro?.title}` : 'Hotel Booking'

  useEffect(() => {
    dispatch(bookingClear())
    dispatch(paymentReset())
  }, [dispatch])


  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Hero Section */}
      <motion.div
        initial={{ scale: 1.1, opacity: 0 }} // เริ่มแบบซูมเข้ามาหน่อยๆ
        animate={{ scale: 1, opacity: 1 }}   // ค่อยๆ กลับสู่ขนาดปกติ
        transition={{ duration: 1.5, ease: "easeOut" }} // ใช้เวลา 1.5 วิ ให้ดูแพง
        style={{
          height: isMobile ? 250 : 350, // เพิ่มความสูงอีกนิดเพื่อความอลังการ
          backgroundImage: `url(${hotelBG})`, // เพิ่ม Gradient จางๆ ให้ตัวหนังสือชัดขึ้น
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
          display: isEmpty(hotelBG) ? 'none' : 'flex',
          alignItems: 'flex-start', // ย้าย Title มาด้านล่างหน่อยจะดู Modern กว่า
          justifyContent: 'flex-start',
          borderRadius: '20px 20px 0 0', // เปลี่ยนเป็นมนด้านล่างแทน หรือถ้าชอบมนบนให้แก้กลับได้ครับ
          padding: isMobile ? 24 : 32,
          overflow: 'hidden'
        }}
      >
        <motion.div
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
        >
          <Title level={isMobile ? 3 : 1} style={{
            color: '#fff',
            position: 'relative',
            zIndex: 1,
            textShadow: '0 2px 4px rgba(0,0,0,0.5)', // เพิ่มเงาให้ลึกขึ้น
            margin: 0
          }}>
            {hotelIntro?.title}
          </Title>
        </motion.div>
      </motion.div>

      {/* Sticky Search Card */}
      <div style={{
        padding: '0 16px',
        marginTop: isMobile ? -40 : -10, // ดึงขึ้นมาทับ Hero มากขึ้น
        position: isMobile ? 'relative' : 'sticky',
        top: 70, // ปรับให้ไม่ชิดขอบบนเกินไปเวลา scroll
        zIndex: isMobile ? 1 : 100,
        pointerEvents: 'none' // ให้คลิกทะลุพื้นที่ว่างได้
      }}>
        <motion.div
          variants={itemVariants}
          style={{ pointerEvents: 'auto' }}
          whileHover={{ y: -5 }} // ลอยขึ้นนิดนึงเมื่อเอาเมาส์ไปวาง
        >
          <div style={{ boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}> {/* เพิ่มเงาฟุ้งๆ */}
            <BookingSearchCard mode={'mobile'} propcode={propcode} />
          </div>
        </motion.div>
      </div>

      {/* Content Section */}
      <div style={{ padding: isMobile ? 16 : "0 24px 24px 24px", maxWidth: 1080, margin: '0 auto' }}>
        <Space orientation="vertical" size={24} style={{ width: '100%' }}> {/* เพิ่ม gap ระหว่าง section */}

          <motion.div variants={itemVariants}>
            <HotelIntro />
          </motion.div>

          <motion.div variants={itemVariants}>
            <HotelHeroCarousel />
          </motion.div>
          
          <motion.div variants={itemVariants}>
            <HotelPhotoGrid />
          </motion.div>

          {hotelIntro?.facilities && hotelIntro?.facilities.length > 0 &&
            <motion.div variants={itemVariants}>
              <Card
                title={intl.formatMessage({ id: 'room.facilities', defaultMessage: 'สิ่งอำนวยความสะดวก' })}
                style={{ borderRadius: 16, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}
              >
                <FacilitiesList facilities={hotelIntro?.facilities} />
              </Card>
            </motion.div>
          }

          <motion.div variants={itemVariants}>
            <Card
              title={intl.formatMessage({ id: 'booking.roomTypes', defaultMessage: 'ประเภทห้องพักที่มีให้บริการ' })}
              styles={{
                root: {
                  borderRadius: 16,
                  border: 'none',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                },
                header: {
                  borderBottom: '1px solid #f0f0f0',
                  padding: '0 24px'
                }
              }}
            >
              <HotelRoomOverview rooms={hotelRooms} />
            </Card>
          </motion.div>

          <motion.div variants={itemVariants}>
            <GoogleMapMockup mapUrl={mapUrlEmbed} />
          </motion.div>

          <motion.div variants={itemVariants}>
            <HotelContactInfo />
          </motion.div>


        </Space>
      </div>
    </motion.div>
  )
}

export default HotelLanding;